


































































import {
  defineComponent,
  PropType,
  ref,
  onMounted,
  watch,
} from '@vue/composition-api';
import { createTabletRenderer, TabletRenderer } from '@oare/oare';
import {
  EpigraphicUnit,
  TextDiscourseRow,
  EpigraphicWord,
  SearchSpellingResultRow,
  LocaleCode,
  EditorDiscourseWord,
} from '@oare/types';
import { formatLineNumber, romanNumeral } from '@oare/oare/src/tabletUtils';
import sl from '@/serviceLocator';
import i18n from '@/i18n';
import ConnectDiscourseItem from '@/views/Texts/CollectionTexts/AddTexts/Discourse/components/ConnectDiscourseItem.vue';

export default defineComponent({
  props: {
    epigraphicUnits: {
      type: Array as PropType<EpigraphicUnit[]>,
      required: true,
    },
    discourseRows: {
      type: Array as PropType<TextDiscourseRow[]>,
      required: true,
    },
    manualDiscourseSelections: {
      type: Array as PropType<string[]>,
      required: true,
    },
  },
  components: {
    ConnectDiscourseItem,
  },
  setup(props, { emit }) {
    const store = sl.get('store');
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');

    const loading = ref(false);

    const renderer = ref<TabletRenderer>(
      createTabletRenderer(props.epigraphicUnits, i18n.locale as LocaleCode, {
        showNullDiscourse: store.getters.isAdmin,
        textFormat: 'html',
      })
    );

    const lineNumber = (line: number): string => {
      if (
        renderer.value.isRegion(line) ||
        renderer.value.isUndetermined(line)
      ) {
        return '';
      }
      const lineNumber = formatLineNumber(line);
      return lineNumber;
    };

    const setSpelling = (
      word: EpigraphicWord,
      form: SearchSpellingResultRow | undefined
    ) => {
      const newDiscourseRows = props.discourseRows.map(row => {
        if (row.uuid !== word.discourseUuid) {
          return row;
        }
        return {
          ...row,
          spellingUuid: form ? form.spellingUuid : undefined,
          transcription: form ? form.form.form : undefined,
        };
      });
      emit('update-discourse-rows', newDiscourseRows);
      emit('update-manual-selections', word.discourseUuid);
    };

    const searchSpellingResults = ref<{
      [key: string]: SearchSpellingResultRow[];
    }>({});

    onMounted(async () => {
      try {
        loading.value = true;
        const discourseRowsWithSpelling = props.discourseRows.filter(
          row => row.explicitSpelling
        );
        const forms = await Promise.all(
          discourseRowsWithSpelling.map(row =>
            server.searchSpellings(row.explicitSpelling || '')
          )
        );

        discourseRowsWithSpelling.map((row, idx) => {
          searchSpellingResults.value[row.uuid] = forms[idx];
        });
      } catch (err) {
        actions.showErrorSnackbar(
          'Error loading discourse forms. Please try again.',
          err as Error
        );
      } finally {
        loading.value = false;
      }
    });

    const getSelectedForm = (discourseUuid: string) => {
      const spellingUuid = props.discourseRows.filter(
        row => row.uuid === discourseUuid
      )[0].spellingUuid;
      if (!spellingUuid) {
        return '--';
      }

      const discourseForms = searchSpellingResults.value[discourseUuid];
      if (!discourseForms) {
        return '--';
      }

      const relevantForm = discourseForms.filter(
        form => form.spellingUuid === spellingUuid
      )[0].form.form;
      return relevantForm;
    };

    const getColor = (discourseUuid: string | null) => {
      if (!discourseUuid) {
        return 'red';
      }
      const spellingUuid = props.discourseRows.filter(
        row => row.uuid === discourseUuid
      )[0].spellingUuid;
      if (spellingUuid) {
        if (
          props.manualDiscourseSelections.includes(discourseUuid) ||
          (searchSpellingResults.value[discourseUuid] &&
            searchSpellingResults.value[discourseUuid].length === 1)
        ) {
          return 'green';
        }
      }

      const discourseForms = searchSpellingResults.value[discourseUuid];
      if (!discourseForms || discourseForms.length === 0) {
        return 'red';
      }

      return 'yellow';
    };

    const isNumber = (discourseUuid: string) => {
      const discourseRow = props.discourseRows.filter(
        row => row.uuid === discourseUuid
      )[0];
      return discourseRow.type === 'number';
    };

    const isUndetermined = (reading: string) => {
      return reading.includes('...') || reading.match(/x+/);
    };

    const isSeparator = (reading: string) => {
      return reading === '|';
    };

    const getDiscourseWord = (word: EpigraphicWord): EditorDiscourseWord => {
      return {
        discourseUuid: word.discourseUuid,
        spelling:
          props.discourseRows.filter(row => row.uuid === word.discourseUuid)
            .length > 0
            ? props.discourseRows.filter(
                row => row.uuid === word.discourseUuid
              )[0].explicitSpelling || ''
            : '',
        type: word.signs.every(sign => sign.epigType === 'number')
          ? 'number'
          : 'word',
      };
    };

    const formsLoading = ref<string[]>([]);
    const loadingForms = (word: EpigraphicWord) => {
      if (word.discourseUuid) {
        formsLoading.value = formsLoading.value.filter(
          uuid => uuid != word.discourseUuid
        );
        formsLoading.value = [...formsLoading.value, word.discourseUuid];
      }
    };
    const loadedForms = (word: EpigraphicWord) => {
      formsLoading.value = formsLoading.value.filter(
        uuid => uuid != word.discourseUuid
      );
    };
    watch(formsLoading, () => {
      if (formsLoading.value.length === 0) {
        emit('step-complete', true);
      } else {
      }
    });

    return {
      renderer,
      lineNumber,
      setSpelling,
      loading,
      searchSpellingResults,
      getSelectedForm,
      getColor,
      isNumber,
      isUndetermined,
      isSeparator,
      romanNumeral,
      getDiscourseWord,
      formsLoading,
      loadingForms,
      loadedForms,
    };
  },
});
